// Noms des sujets Realtime de la webapp (contrat du plan A) et choix des
// sujets de ligue d'un utilisateur.
import type { League, User } from '../../types';
import { isAdminOrSuperAdmin } from '../auth/isSuperAdmin';

export const SYSTEM_TOPIC = 'system';
export const ADMIN_TOPIC = 'admin';

// Même forme que can_join_realtime_topic (migration 20261013100000) : uuid en minuscules.
const TOPIC_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function normalizeTopicId(id: unknown): string | null {
    if (typeof id !== 'string') return null;
    const lower = id.trim().toLowerCase();
    return TOPIC_ID_RE.test(lower) ? lower : null;
}

export const leagueTopic = (leagueId: string): string => `league:${leagueId}`;
export const userTopic = (userId: string): string => `user:${userId}`;

type TopicUser = Pick<User, 'role' | 'primaryRole' | 'roles' | 'leagueIds'>;
type TopicLeague = Pick<League, 'id' | 'is_active'>;

/**
 * Sujets `league:<id>` à rejoindre. Responsable de ligue : ses ligues
 * (user_league_roles, comme get_user_league_ids()). Administrateur : les
 * ligues actives et la ligue choisie dans l'en-tête — pas les 73 ligues
 * (décision D2 du plan B) ; les autres se rafraîchissent au focus.
 */
export function leagueTopicsFor(
    user: TopicUser | null | undefined,
    leagues: readonly TopicLeague[] | undefined,
    currentLeagueId: string | null | undefined,
): string[] {
    if (!user) return [];
    const ids = new Set<string>();
    const add = (id: unknown) => {
        const normalized = normalizeTopicId(id);
        if (normalized) ids.add(normalized);
    };
    if (isAdminOrSuperAdmin(user as User)) {
        for (const league of leagues ?? []) if (league.is_active !== false) add(league.id);
        add(currentLeagueId);
    } else {
        for (const id of user.leagueIds ?? []) add(id);
    }
    return [...ids].sort().map(leagueTopic);
}

/** `admin` : is_admin() ou un rôle de ligue (can_join_realtime_topic). */
export function canJoinAdminTopic(user: TopicUser | null | undefined): boolean {
    if (!user) return false;
    return isAdminOrSuperAdmin(user as User) || (user.leagueIds ?? []).some((id) => normalizeTopicId(id) !== null);
}
